import {
  ComplexEntityTypes,
  LogicNameFilter,
} from "../../../../utils/constants";

export const ALL_OPTION_NAME = "All";

export const filterName = {
  SEARCH_STRING: "searchString",
  RISK: "risk",
  SOURCES: "sources",
  ENTITY_TYPE: "entityType",
  EXCLUDE_SOURCE: "excluded_sources",
  RELATED_ASSETS: "relatedAssets",
  CVE: "cve",
  HOST_OS: "hostOS",
  HOST_OS_VERSION: "hostOSVersion",
  HOST_OS_FULL_VERSION: "hostOSFullVersion",
  HOST_LAST_SEEN: "hostLastSeen",
  CUSTOMER_ID: "customerId",
  DEPARTMENT: "department",
  PRIVILEGED_IN: "privilegedIn",
  OKTA_ADMINS: "oktaAdmins",
  OKTA_GROUPS: "oktaGroups",
  OKTA_STATUS: "oktaStatus",
  POLICIES: "policies",
  LAST_ACTIVE: "lastActive",
  TERMINATION_DATE: "terminationDate",
  SERVER_TYPE: "serverType",
  SERVER_OS: "serverOS",
  SERVER_OS_VERSION: "serverOSVersion",
  SERVER_IMAGE: "serverImage",
  INTERNET_EXPOSURE: "internetExposure",
  DATASTORE_TYPE: "datastoreType",
  BACKUP: "backup",
  NHI_TYPE: "NHIType",
  NHI_LAST_USED: "NHILastUsed",
  EXPIRATION_DATE: "expirationDate",
  ENTITY_IDS: "entityIds",
  HAS_LOCAL_ADMIN_USER: "hasLocalAdminUser",
  NHI_ROTATION_ENABLED: "NHIRotationEnabled",
  NHI_POLICIES: "NHIPolicies",
  APP_USING_AI: "appUsingAI",
  MY_FAVORITES: "myFavorites",
  LABELS: "labels",
  PUBLIC_ACCESS: "publicAccess",
  CONNECTED_HOSTS_COUNT: "connectedHostsCount",
  SECURITY_GROUPS: "securityGroups",
  SERVICENOW_COMPANY: "servicenowCompany",
  SERVICENOW_RESPONSIBLE_GROUP: "servicenowResponsibleGroup",
  SERVICENOW_MOST_FREQUENT_USER: "servicenowMostFrequentUser",
  SERVICENOW_CROWN_JEWEL: "servicenowCrownJewel",
  SERVICENOW_IS_GLOBAL: "servicenowIsGlobal",
  SERVICENOW_PCI_CDE: "servicenowPciCde",
  SERVICENOW_PCI_SUPPORTING: "servicenowPciSupporting",
  SERVICENOW_CCPA: "servicenowCcpa",
  SERVICENOW_SOX: "servicenowSox",
  SERVICENOW_PII: "servicenowPii",
  SERVICENOW_GDPR: "servicenowGdpr",
  SERVICENOW_OWNING_COMPANY_SYS_ID: "servicenowOwningCompanySysId",
  SERVICENOW_OWNING_SERVICE_OWNING_ORGANIZATION:
    "servicenowOwningServiceOwningOrganization",
  SERVICENOW_ENVIRONMENT: "servicenowEnvironment",
  SERVICENOW_SYS_TAGS: "servicenowSysTags",
  SERVICENOW_APPLICATION_ID_TAG: "servicenowApplicationIdTag",
  SERVICENOW_OWNING_SERVICE_OWNED_BY: "servicenowOwningServiceOwnedBy",
  SERVICENOW_OWNING_SERVICE_USED_BY_COMPANIES:
    "servicenowOwningServiceUsedByCompanies",
  SERVICENOW_USED_BY_COMPANIES: "servicenowUsedByCompanies",
  SERVICENOW_OWNING_SERVICE_MANAGED_BY: "servicenowOwningServiceManagedBy",
  SERVICENOW_IT_APPLICATION_OWNER: "servicenowItApplicationOwner",
  SERVICENOW_OWNING_SERVICE_ASSIGNMENT_GROUP:
    "servicenowOwningServiceAssignmentGroup",
  SERVICENOW_PCI_SUPPORTING_ASSET: "servicenowPciSupportingAsset",
  SERVICENOW_SYS_CLASS_NAME: "servicenowSysClassName",
  SERVICENOW_ISO_27001: "servicenowIso27001",
  CUSTOM_TAGS: "customTags",
  NHI_PRIVILEGED_IN_AD: "nhiPrivilegedInAD",
  NHI_CONNECTED_EMAIL: "nhiConnectedEmail",
};

export const hasAnyFiltersApplied = (appliedFilters) => {
  return Object.entries(appliedFilters).some(([filterKey, filterValue]) => {
    // Handle different filter value types
    if (filterValue === null || filterValue === undefined) return false;

    if (typeof filterValue === "boolean") {
      return filterValue; // true = filter applied, false = no filter
    }

    if (typeof filterValue === "string") {
      return filterValue.length > 0; // non-empty string = filter applied
    }

    if (Array.isArray(filterValue)) {
      return filterValue.length > 0; // non-empty array = filter applied
    }

    if (typeof filterValue === "object") {
      // Object with keys = filter applied, empty object = no filter
      return Object.keys(filterValue).length > 0;
    }

    // For any other type, convert to boolean
    return Boolean(filterValue);
  });
};

const getInitialTypeSpecificFilters = (entityType) => {
  switch (entityType) {
    case ComplexEntityTypes.HOST:
      return {
        [filterName.HOST_OS]: {},
        [filterName.HOST_OS_FULL_VERSION]: {},
        [filterName.HOST_OS_VERSION]: {},
        [filterName.HOST_LAST_SEEN]: {},
      };
    case ComplexEntityTypes.USER:
      return {
        [filterName.DEPARTMENT]: {},
        [filterName.PRIVILEGED_IN]: {},
        [filterName.OKTA_ADMINS]: {},
        [filterName.OKTA_GROUPS]: {},
        [filterName.OKTA_STATUS]: {},
        [filterName.POLICIES]: {},
        [filterName.LAST_ACTIVE]: {},
        [filterName.TERMINATION_DATE]: {},
        [filterName.CONNECTED_HOSTS_COUNT]: {},
      };
    case ComplexEntityTypes.SERVER:
      return {
        [filterName.SERVER_TYPE]: {},
        [filterName.SERVER_OS]: {},
        [filterName.SERVER_OS_VERSION]: {},
        [filterName.INTERNET_EXPOSURE]: {},
      };
    case ComplexEntityTypes.DATA_STORE:
      return {
        [filterName.DATASTORE_TYPE]: {},
        [filterName.BACKUP]: {},
      };
    case ComplexEntityTypes.NONHUMAN_IDENTITY:
      return {
        [filterName.NHI_TYPE]: {},
        [filterName.NHI_LAST_USED]: {},
        [filterName.EXPIRATION_DATE]: {},
        [filterName.NHI_ROTATION_ENABLED]: {},
        [filterName.NHI_POLICIES]: {},
        [filterName.NHI_PRIVILEGED_IN_AD]: {},
        [filterName.NHI_CONNECTED_EMAIL]: {},
      };
    case ComplexEntityTypes.APP:
      return {
        [filterName.APP_USING_AI]: false,
      };
    default:
      return {};
  }
};

export const getInitialFilters = (entityType = null) => ({
  [filterName.SEARCH_STRING]: "",
  [filterName.RISK]: {},
  [filterName.SOURCES]: {},
  [filterName.EXCLUDE_SOURCE]: {},
  [filterName.RELATED_ASSETS]: {},
  [filterName.CVE]: {}, // Used for host and server entities
  [filterName.ENTITY_IDS]: {},
  [filterName.HAS_LOCAL_ADMIN_USER]: {},
  [filterName.MY_FAVORITES]: false,
  [filterName.LABELS]: {},
  [filterName.CUSTOMER_ID]: {},
  [filterName.SERVICENOW_COMPANY]: {},
  [filterName.SERVICENOW_RESPONSIBLE_GROUP]: {},
  [filterName.SERVICENOW_MOST_FREQUENT_USER]: {},
  [filterName.SERVICENOW_CROWN_JEWEL]: {},
  [filterName.SERVICENOW_IS_GLOBAL]: {},
  [filterName.SERVICENOW_PCI_CDE]: {},
  [filterName.SERVICENOW_PCI_SUPPORTING]: {},
  [filterName.SERVICENOW_CCPA]: {},
  [filterName.SERVICENOW_SOX]: {},
  [filterName.SERVICENOW_PII]: {},
  [filterName.SERVICENOW_GDPR]: {},
  [filterName.SERVICENOW_OWNING_COMPANY_SYS_ID]: {},
  [filterName.SERVICENOW_OWNING_SERVICE_OWNING_ORGANIZATION]: {},
  [filterName.SERVICENOW_ENVIRONMENT]: {},
  [filterName.SERVICENOW_SYS_TAGS]: {},
  [filterName.SERVICENOW_APPLICATION_ID_TAG]: {},
  [filterName.SERVICENOW_OWNING_SERVICE_OWNED_BY]: {},
  [filterName.SERVICENOW_OWNING_SERVICE_USED_BY_COMPANIES]: {},
  [filterName.SERVICENOW_USED_BY_COMPANIES]: {},
  [filterName.SERVICENOW_OWNING_SERVICE_MANAGED_BY]: {},
  [filterName.SERVICENOW_IT_APPLICATION_OWNER]: {},
  [filterName.SERVICENOW_OWNING_SERVICE_ASSIGNMENT_GROUP]: {},
  [filterName.SERVICENOW_PCI_SUPPORTING_ASSET]: {},
  [filterName.SERVICENOW_SYS_CLASS_NAME]: {},
  [filterName.SERVICENOW_ISO_27001]: {},
  [filterName.CUSTOM_TAGS]: {},
  ...(entityType ? getInitialTypeSpecificFilters(entityType) : {}),
});

const getInitialTypeSpecificLogics = (entityType) => {
  switch (entityType) {
    case ComplexEntityTypes.HOST:
      return {
        [filterName.HOST_LAST_SEEN]: LogicNameFilter.TIME_AFTER,
      };
    case ComplexEntityTypes.USER:
      return {
        [filterName.PRIVILEGED_IN]: LogicNameFilter.LOGIC_AND,
        [filterName.OKTA_ADMINS]: LogicNameFilter.LOGIC_AND,
        [filterName.OKTA_GROUPS]: LogicNameFilter.LOGIC_AND,
        [filterName.OKTA_STATUS]: LogicNameFilter.LOGIC_AND,
        [filterName.POLICIES]: LogicNameFilter.LOGIC_OR,
        [filterName.LAST_ACTIVE]: LogicNameFilter.TIME_AFTER,
        [filterName.TERMINATION_DATE]: LogicNameFilter.TIME_BEFORE,
      };
    case ComplexEntityTypes.NONHUMAN_IDENTITY:
      return {
        [filterName.NHI_LAST_USED]: LogicNameFilter.TIME_AFTER,
        [filterName.NHI_POLICIES]: LogicNameFilter.LOGIC_OR,
        [filterName.NHI_CONNECTED_EMAIL]: LogicNameFilter.LOGIC_OR,
      };
    case ComplexEntityTypes.DATA_STORE:
      return {
        [filterName.PUBLIC_ACCESS]: LogicNameFilter.LOGIC_AND,
      };
    default:
      return {};
  }
};

export const getInitialLogics = (entityType = null) => ({
  [filterName.SOURCES]: LogicNameFilter.LOGIC_AND,
  [filterName.RELATED_ASSETS]: LogicNameFilter.LOGIC_AND,
  [filterName.LABELS]: LogicNameFilter.LOGIC_AND,
  [filterName.CUSTOM_TAGS]: LogicNameFilter.LOGIC_AND,
  ...(entityType ? getInitialTypeSpecificLogics(entityType) : {}),
});

export const pastTimeOption = {
  LAST_7_DAYS: "last_7_days",
  LAST_14_DAYS: "last_14_days",
  LAST_30_DAYS: "last_30_days",
  LAST_90_DAYS: "last_90_days",
  LAST_6_MONTHS: "last_6_months",
  LAST_12_MONTHS: "last_12_months",
};

export const pastTimeValues = {
  [pastTimeOption.LAST_7_DAYS]: { label: "7 Days", days: 7 },
  [pastTimeOption.LAST_14_DAYS]: { label: "14 Days", days: 14 },
  [pastTimeOption.LAST_30_DAYS]: { label: "30 Days", days: 30 },
  [pastTimeOption.LAST_90_DAYS]: { label: "90 Days", days: 90 },
  [pastTimeOption.LAST_6_MONTHS]: { label: "6 Months", days: 182 },
  [pastTimeOption.LAST_12_MONTHS]: { label: "12 Months", days: 365 },
};

export const futureTimeOption = {
  IN_7_DAYS: "in_7_days",
  IN_14_DAYS: "in_14_days",
  IN_30_DAYS: "in_30_days",
  IN_90_DAYS: "in_90_days",
  IN_6_MONTHS: "in_6_months",
};

export const futureTimeValues = {
  [futureTimeOption.IN_7_DAYS]: { label: "In 7 Days", days: 7 },
  [futureTimeOption.IN_14_DAYS]: { label: "In 14 Days", days: 14 },
  [futureTimeOption.IN_30_DAYS]: { label: "In 30 Days", days: 30 },
  [futureTimeOption.IN_90_DAYS]: { label: "In 90 Days", days: 90 },
  [futureTimeOption.IN_6_MONTHS]: { label: "In 6 Months", days: 182 },
};

export const terminationTimeOption = {
  ALREADY_TERMINATED: "already_terminated",
  ALL_UPCOMING: "all_upcoming",
  NEXT_7_DAYS: "next_7_days",
  NEXT_14_DAYS: "next_14_days",
  NEXT_30_DAYS: "next_30_days",
};

export const terminationTimeValues = {
  [terminationTimeOption.ALL_UPCOMING]: {
    label: "All upcoming terminations",
    days: null,
    isPast: false,
  },
  [terminationTimeOption.NEXT_7_DAYS]: {
    label: "Next 7 days",
    days: 7,
    isPast: false,
  },
  [terminationTimeOption.NEXT_14_DAYS]: {
    label: "Next 14 days",
    days: 14,
    isPast: false,
  },
  [terminationTimeOption.NEXT_30_DAYS]: {
    label: "Next 30 days",
    days: 30,
    isPast: false,
  },
};
